import {
  Phone,
  Mail,
  MessageCircle,
  Rocket,
  HeartHandshake,
  ShieldCheck,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Contact() {
  return (
    <section
      id="contacto"
      className="py-16 sm:py-24 lg:py-32 bg-gradient-to-br from-slate-50 to-white dark:from-slate-900/80 dark:to-slate-900 relative overflow-hidden"
    >
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px] -mr-96 -mt-96 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-secondary/5 rounded-full blur-[100px] -ml-64 -mb-64 pointer-events-none" />

      <div className="container px-4 mx-auto relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-bold mb-6">
            <HeartHandshake className="w-4 h-4" />
            <span>Habla con Expertos</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-6 sm:mb-8 tracking-tight leading-tight font-display text-slate-900 dark:text-white">
            Transforma tu negocio{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-blue-600">
              hoy mismo
            </span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-slate-600 dark:text-slate-400 leading-relaxed">
            No estás comprando un software, estás invirtiendo en la tranquilidad
            de tener el control total. Nuestro equipo está listo para
            asesorarte.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 max-w-5xl mx-auto">
          {/* Valor / Beneficios */}
          <div className="flex flex-col justify-center space-y-6 sm:space-y-8">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0 text-blue-600 dark:text-blue-400">
                <Rocket className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white mb-1 sm:mb-2">
                  Instalación Inmediata
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                  Comienza a usar QvaPOS el mismo día. Te guiamos paso a paso en
                  la configuración inicial.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0 text-green-600 dark:text-green-400">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                  Asesoría Personalizada
                </h3>
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                  Evaluamos tu tipo de negocio (tienda, ferretería, alimentos)
                  para adaptar el sistema a tus necesidades.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0 text-orange-600 dark:text-orange-400">
                <Zap className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                  Soporte Directo
                </h3>
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                  Olvídate de los tickets lentos. Te atendemos directamente por
                  WhatsApp con respuestas rápidas.
                </p>
              </div>
            </div>
          </div>

          {/* Tarjeta de Contacto Principal */}
          <div className="bg-white dark:bg-slate-800/80 rounded-2xl sm:rounded-[2rem] lg:rounded-[2.5rem] p-6 sm:p-8 lg:p-10 shadow-2xl border border-slate-100 dark:border-slate-700 backdrop-blur-xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/10 rounded-bl-full transition-transform group-hover:scale-110" />

            <div className="relative z-10 text-center space-y-8">
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-green-100 dark:bg-green-900/30 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto shadow-inner">
                <MessageCircle className="w-10 h-10 sm:w-12 sm:h-12 text-green-600 dark:text-green-400" />
              </div>

              <div>
                <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-3">
                  Atención vía WhatsApp
                </h3>
                <p className="text-slate-500 dark:text-slate-400 text-lg mb-6">
                  Escríbenos o llámanos directamente. Respondemos al instante.
                </p>
                <div className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2 sm:py-3 rounded-xl sm:rounded-2xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800">
                  <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                  <a
                    href="https://wa.me/5354882577"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xl sm:text-2xl lg:text-3xl font-black tracking-wider text-slate-900 dark:text-white hover:text-green-600 dark:hover:text-green-400 transition-colors whitespace-nowrap"
                  >
                    +53 5488 2577
                  </a>
                </div>
              </div>

              <div className="pt-4">
                <Button
                  onClick={() =>
                    window.open("https://wa.me/5354882577", "_blank")
                  }
                  className="w-full h-12 sm:h-14 lg:h-16 rounded-full font-black text-base sm:text-lg bg-green-600 hover:bg-green-700 text-white shadow-xl shadow-green-600/20 hover:scale-[1.02] transition-all"
                >
                  <MessageCircle className="mr-2 sm:mr-3 w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
                  Chat Directo en WhatsApp
                </Button>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-4 font-medium uppercase tracking-wider">
                  DISPONIBILIDAD LUNES A SÁBADO
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
